import { useState, useEffect } from 'react';
import { DollarSign, Calendar, User, Plus, FileText, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { adelantosApi, mecanicosApi } from '../lib/api';

interface Adelanto {
  id: string;
  fecha: string;
  mecanico_id: string;
  mecanico_nombre: string;
  monto: number;
  motivo: string;
  created_at: string;
}

const Adelantos = () => {
  const [adelantos, setAdelantos] = useState<Adelanto[]>([]);
  const [mecanicos, setMecanicos] = useState<any[]>([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [cargando, setCargando] = useState(true);
  
  const [nuevoAdelanto, setNuevoAdelanto] = useState({
    fecha: format(new Date(), 'yyyy-MM-dd'),
    mecanico_id: '',
    monto: '',
    motivo: '',
    caja: 'mantenimiento' as '' | 'mantenimiento' | 'ventas' | 'servicios'   // sueldos y adelantos salen siempre de Mantenimiento
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [dataAdelantos, dataMecanicos] = await Promise.all([
        adelantosApi.getAll(),
        mecanicosApi.getAll()
      ]);
      setAdelantos(dataAdelantos || []);
      // Los mecanicos dados de baja no deben poder recibir adelantos nuevos.
      setMecanicos((dataMecanicos || []).filter((m: any) => m.estado !== 'inactivo'));
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setCargando(false);
    }
  };

  const guardarAdelanto = async () => {
    if (!nuevoAdelanto.mecanico_id || !nuevoAdelanto.monto || !nuevoAdelanto.motivo) {
      alert('Por favor complete todos los campos');
      return;
    }

    try {
      await adelantosApi.create({
        fecha: nuevoAdelanto.fecha,
        mecanico_id: nuevoAdelanto.mecanico_id,
        monto: parseFloat(nuevoAdelanto.monto),
        motivo: nuevoAdelanto.motivo,
        caja: nuevoAdelanto.caja
      });
      setMostrarModal(false);
      setNuevoAdelanto({
        fecha: format(new Date(), 'yyyy-MM-dd'),
        mecanico_id: '',
        monto: '',
        motivo: '',
        caja: 'mantenimiento'
      });
      cargarDatos();
    } catch (error) {
      console.error('Error guardando adelanto:', error);
      alert('Error al guardar');
    }
  };

  const eliminarAdelanto = async (id: string) => {
    if (!confirm('¿Está seguro de eliminar este adelanto?')) return;

    try {
      await adelantosApi.delete(id);
      cargarDatos();
    } catch (error) {
      console.error('Error eliminando adelanto:', error);
      alert('Error al eliminar');
    }
  };

  const totalAdelantos = adelantos.reduce((sum, a) => sum + a.monto, 0);

  if (cargando) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <DollarSign className="text-green-600" />
            Adelantos de Mecánicos
          </h1>
          <p className="text-gray-600 mt-1">Registro de adelantos de sueldo</p>
        </div>
        <button
          onClick={() => setMostrarModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Registrar Adelanto
        </button>
      </div>

      {/* Total de adelantos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Adelantos</p>
              <p className="text-3xl font-bold mt-2">S/ {totalAdelantos.toFixed(2)}</p>
            </div>
            <DollarSign size={48} className="text-green-200 opacity-50" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Cantidad de Adelantos</p>
              <p className="text-3xl font-bold mt-2 text-gray-800">{adelantos.length}</p>
            </div>
            <FileText size={48} className="text-gray-300" />
          </div>
        </div>
      </div>

      {/* Lista de adelantos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mecánico
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motivo
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {adelantos.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No hay adelantos registrados
                  </td>
                </tr>
              ) : (
                adelantos.map((adelanto) => (
                  <tr key={adelanto.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(adelanto.fecha), "dd 'de' MMMM, yyyy", { locale: es })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{adelanto.mecanico_nombre}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-green-600">S/ {adelanto.monto.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {adelanto.motivo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => eliminarAdelanto(adelanto.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para registrar adelanto */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Registrar Adelanto</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar size={16} className="inline mr-1" />
                  Fecha
                </label>
                <input
                  type="date"
                  value={nuevoAdelanto.fecha}
                  onChange={(e) => setNuevoAdelanto({ ...nuevoAdelanto, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User size={16} className="inline mr-1" />
                  Mecánico
                </label>
                <select
                  value={nuevoAdelanto.mecanico_id}
                  onChange={(e) => setNuevoAdelanto({ ...nuevoAdelanto, mecanico_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Seleccionar mecánico...</option>
                  {mecanicos.map((m) => (
                    <option key={m.id} value={m.id}>{m.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DollarSign size={16} className="inline mr-1" />
                  Monto (S/)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={nuevoAdelanto.monto}
                  onChange={(e) => setNuevoAdelanto({ ...nuevoAdelanto, monto: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText size={16} className="inline mr-1" />
                  Motivo
                </label>
                <textarea
                  value={nuevoAdelanto.motivo}
                  onChange={(e) => setNuevoAdelanto({ ...nuevoAdelanto, motivo: e.target.value })}
                  placeholder="Motivo del adelanto..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DollarSign size={16} className="inline mr-1" />
                  ¿De qué caja sale este adelanto?
                </label>
                <p className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-700">Caja de Mantenimiento</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setMostrarModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={guardarAdelanto}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Adelantos;
